import React, { useCallback, useEffect, useRef, useState } from 'react';
import { animate, motion, useMotionValue, useReducedMotion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Play } from 'lucide-react';
import type { CardMedia } from '@/lib/library/libraryCard';
import { cn } from '@/lib/utils';

const SPRING = { type: 'spring' as const, stiffness: 500, damping: 38 };
/** A swipe past this share of the width, or faster than this (px/ms), turns the slide. */
const TURN_SHARE = 0.2;
const TURN_SPEED = 0.35;
/** Dots shown at most; longer carousels slide a window of them. */
const MAX_DOTS = 7;

/**
 * The slides of a carousel post (or a multi-photo tweet), swiped one at a
 * time like the app they came from: the track follows the finger, turns on
 * a fifth of the width or a flick, stretches at the ends, and settles on the
 * house spring. Dots under the picture and a counter in the corner say where
 * you are; arrows appear for a mouse.
 *
 * Carries `data-detail-carousel`: the detail view leaves horizontal swipes
 * that start here to the carousel, and still takes vertical ones (dismiss).
 */
export function SwipeCarousel({
  media = [],
  label,
  onIndexChange,
  count: slideCount,
  renderSlide,
}: {
  media?: CardMedia[];
  label: string;
  onIndexChange?: (index: number) => void;
  /** With renderSlide: how many slides (PDF pages) instead of pictures. */
  count?: number;
  /** Draws slide i in place of a picture; `near` is true for the current slide and its neighbours. */
  renderSlide?: (i: number, near: boolean) => React.ReactNode;
}) {
  const reduce = useReducedMotion() ?? false;
  const rootRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const indexRef = useRef(0);
  const x = useMotionValue(0);
  const count = renderSlide ? slideCount ?? 0 : media.length;

  const widthOf = () => rootRef.current?.clientWidth || 1;

  const goTo = useCallback(
    (next: number, velocity = 0) => {
      const i = Math.max(0, Math.min(count - 1, next));
      indexRef.current = i;
      setIndex(i);
      onIndexChange?.(i);
      const target = -i * widthOf();
      if (reduce) x.set(target);
      else animate(x, target, { ...SPRING, velocity });
    },
    [count, onIndexChange, reduce, x],
  );

  // Keep the track aligned if the box is resized (rotation, the open flight settling)
  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let lastWidth = el.clientWidth;
    const observer = new ResizeObserver(() => {
      const w = el.clientWidth;
      if (Math.abs(w - lastWidth) <= 1) return;
      lastWidth = w;
      x.set(-indexRef.current * w);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [x]);

  // Touch: lock to horizontal after a few pixels, follow the finger, turn or spring back
  useEffect(() => {
    const el = rootRef.current;
    if (!el || count < 2) return;
    let startX = 0;
    let startY = 0;
    let base = 0;
    let mode: 'pending' | 'swipe' | 'pass' = 'pass';
    let last = { x: 0, t: 0 };
    let velocity = 0;

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) {
        mode = 'pass';
        return;
      }
      const t = e.touches[0];
      startX = t.clientX;
      startY = t.clientY;
      base = x.get();
      last = { x: t.clientX, t: performance.now() };
      velocity = 0;
      mode = 'pending';
    };
    const onMove = (e: TouchEvent) => {
      if (mode === 'pass') return;
      const t = e.touches[0];
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      if (mode === 'pending') {
        if (Math.hypot(dx, dy) < 8) return;
        mode = Math.abs(dx) > Math.abs(dy) * 1.2 ? 'swipe' : 'pass';
        if (mode === 'pass') return;
      }
      if (e.cancelable) e.preventDefault();
      const now = performance.now();
      velocity = 0.7 * ((t.clientX - last.x) / Math.max(1, now - last.t)) + 0.3 * velocity;
      last = { x: t.clientX, t: now };
      const i = indexRef.current;
      const atEdge = (dx > 0 && i === 0) || (dx < 0 && i === count - 1);
      x.set(base + (atEdge ? dx * 0.3 : dx));
    };
    const onEnd = () => {
      if (mode !== 'swipe') {
        mode = 'pass';
        return;
      }
      mode = 'pass';
      const dx = x.get() + indexRef.current * widthOf();
      const fast = Math.abs(velocity) > TURN_SPEED && Math.sign(velocity) === Math.sign(dx);
      const turn = Math.abs(dx) > widthOf() * TURN_SHARE || fast ? (dx < 0 ? 1 : -1) : 0;
      goTo(indexRef.current + turn, velocity * 1000);
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [count, goTo, x]);

  // A window of dots that follows the current slide
  const dotStart = Math.max(0, Math.min(count - MAX_DOTS, index - Math.floor(MAX_DOTS / 2)));
  const dots = Array.from({ length: Math.min(count, MAX_DOTS) }, (_, k) => dotStart + k);

  return (
    <div
      ref={rootRef}
      data-detail-carousel
      className="group/carousel absolute inset-0 overflow-hidden"
      style={{ touchAction: 'pan-y' }}
      role="region"
      aria-roledescription="carousel"
      aria-label={label}
    >
      <motion.div className="flex h-full" style={{ x }}>
        {renderSlide &&
          Array.from({ length: count }, (_, i) => (
            <div key={i} className="relative h-full w-full shrink-0" aria-hidden={i !== index} aria-roledescription="slide" aria-label={`${i + 1} of ${count}`}>
              {renderSlide(i, Math.abs(i - index) <= 1)}
            </div>
          ))}
        {!renderSlide &&
          media.map((m, i) => (
          <div key={`${m.src}-${i}`} className="relative h-full w-full shrink-0" aria-hidden={i !== index} aria-roledescription="slide" aria-label={`${i + 1} of ${count}`}>
            <img
              src={m.src}
              alt=""
              draggable={false}
              decoding="async"
              loading={Math.abs(i - index) <= 1 ? 'eager' : 'lazy'}
              className="absolute inset-0 h-full w-full select-none object-cover"
            />
            {m.video && (
              <span className="absolute left-1/2 top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-paper-raised/85 text-ink shadow-sm">
                <Play className="ml-0.5 h-5 w-5" fill="currentColor" strokeWidth={0} aria-hidden />
              </span>
            )}
          </div>
        ))}
      </motion.div>

      {count > 1 && (
        <>
          <span className="pointer-events-none absolute right-2.5 top-2.5 rounded-full bg-paper-raised/90 px-2 py-0.5 font-mono text-[11px] tabular-nums text-ink shadow-sm" aria-live="polite">
            {index + 1}/{count}
          </span>
          <div className="pointer-events-none absolute inset-x-0 bottom-2.5 flex justify-center">
            <div className="flex items-center gap-1.5 rounded-full bg-paper-raised/80 px-2 py-1.5 shadow-sm">
              {dots.map((i) => (
                <span
                  key={i}
                  className={cn(
                    'rounded-full transition-[width,background-color,opacity] duration-200 ease-out',
                    i === index ? 'h-1.5 w-3 bg-ink' : 'h-1.5 w-1.5 bg-ink/30',
                    // The window's outer dots shrink when there is more beyond them
                    (i === dots[0] && i > 0) || (i === dots[dots.length - 1] && i < count - 1) ? 'scale-75' : '',
                  )}
                />
              ))}
            </div>
          </div>
          {/* Arrows for a mouse; touch swipes */}
          {index > 0 && (
            <button
              type="button"
              onClick={() => goTo(index - 1)}
              aria-label="Previous slide"
              className="absolute left-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-paper-raised/90 text-ink opacity-0 shadow-sm outline-none transition-opacity duration-150 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-signal group-hover/carousel:opacity-100 [@media(hover:none)]:hidden"
            >
              <ChevronLeft className="h-4 w-4" strokeWidth={2} />
            </button>
          )}
          {index < count - 1 && (
            <button
              type="button"
              onClick={() => goTo(index + 1)}
              aria-label="Next slide"
              className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-paper-raised/90 text-ink opacity-0 shadow-sm outline-none transition-opacity duration-150 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-signal group-hover/carousel:opacity-100 [@media(hover:none)]:hidden"
            >
              <ChevronRight className="h-4 w-4" strokeWidth={2} />
            </button>
          )}
        </>
      )}
    </div>
  );
}
